// src/utils/subscriptionRegistry.js

/**
 * Global subscription registry to dedupe Firestore listeners by key.
 *
 * Pattern:
 *   const release = subscribeOnce('serviceCases:active:org=abc', () => onSnapshot(...));
 *   // later
 *   release();
 *
 * Guarantees:
 * - Only one active onSnapshot per key
 * - N callers can share it via ref counting
 * - Unsub happens when last caller releases
 */

const registry = new Map()

/**
 * @param {string} key
 * @param {() => (() => void)} subscribeFn must return the Firestore unsubscribe
 * @returns {() => void} release
 */
export function subscribeOnce(key, subscribeFn) {
  if (!key || typeof key !== 'string') throw new Error('subscribeOnce requires a string key')
  if (typeof subscribeFn !== 'function') throw new Error('subscribeOnce requires a subscribeFn()')

  const existing = registry.get(key)
  if (existing) {
    existing.refCount += 1
    return () => release(key)
  }

  const unsub = subscribeFn()
  if (typeof unsub !== 'function') {
    // Defensive: avoid leaking “registered but not unsubscribable” entries
    throw new Error(`subscribeOnce("${key}") subscribeFn must return an unsubscribe function`)
  }

  registry.set(key, { unsub, refCount: 1, createdAt: Date.now() })
  return () => release(key)
}

export function release(key) {
  const entry = registry.get(key)
  if (!entry) return

  entry.refCount -= 1
  if (entry.refCount > 0) return

  try {
    entry.unsub()
  } finally {
    registry.delete(key)
  }
}

export function resetAllSubscriptions() {
  for (const [key, entry] of registry.entries()) {
    try {
      entry.unsub()
    } catch (e) {
      console.warn('[subscriptionRegistry] failed to unsub for key', key, e)
    }
  }
  registry.clear()
}

export function getActiveSubscriptionKeys() {
  return Array.from(registry.keys())
}
